/**
 * ExhibitorBooths - CRUD for exhibitor booth management
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Loader2, Globe, Building2, Upload, X } from 'lucide-react';
import { exhibitorAPI } from '@/lib/api';
import api from '@/lib/api';
import GoogleMapPicker from '@/components/GoogleMapPicker';
import { MediaUpload } from '@/components/MediaUpload';

const BOOTH_SIZES = ['small', 'medium', 'large', 'custom'];
const SIZE_LABELS = { small: '10x10', medium: '10x20', large: '20x20', custom: 'Custom' };

// Form component extracted outside
const ExhibitorForm = ({ exhibitor, onSubmit, onCancel }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(exhibitor || { 
    company_name: '', 
    contact_name: '', 
    email: '', 
    phone: '', 
    website_url: '', 
    logo_url: '',
    description: '', 
    booth_number: '', 
    booth_size: 'medium',
    category: '',
    products_services: [],
    is_featured: false,
    // Location fields
    address: '',
    latitude: null,
    longitude: null
  });
  const [productInput, setProductInput] = useState('');
  
  const addProduct = () => { 
    if (productInput.trim()) { 
      setForm({...form, products_services: [...(form.products_services || []), productInput.trim()]}); 
      setProductInput(''); 
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'general');

      const response = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        setForm({...form, logo_url: fullUrl});
        toast.success('Logo uploaded!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Tabs defaultValue="company" className="w-full">
      <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 mb-4">
        <TabsTrigger value="company">Company</TabsTrigger>
        <TabsTrigger value="booth">Booth & Location</TabsTrigger>
        <TabsTrigger value="contact">Contact</TabsTrigger>
      </TabsList>
      
      <div className="max-h-[60vh] overflow-y-auto">
        <TabsContent value="company" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Company Name *</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
            <div><Label>Category</Label><Input value={form.category || ''} onChange={e => setForm({...form, category: e.target.value})} placeholder="Technology, Healthcare..." /></div>
          </div>
          <div><Label>Website</Label><Input value={form.website_url || ''} onChange={e => setForm({...form, website_url: e.target.value})} placeholder="https://..." /></div>
          
          {/* Logo upload section */}
          <div className="space-y-2">
            <MediaUpload value={form.logo_url || ''} onChange={(url) => setForm({ ...form, logo_url: url })} purpose="logo" label="Logo" testId="booth-logo-upload" previewClassName="h-16" />
          </div>

          <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={2} /></div>
          <div>
            <Label>Products & Services</Label>
            <div className="flex gap-2">
              <Input value={productInput} onChange={e => setProductInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProduct())} placeholder="Add product/service..." />
              <Button type="button" variant="outline" onClick={addProduct}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(form.products_services || []).map((item, i) => (
                <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setForm({...form, products_services: form.products_services.filter((_, j) => j !== i)})}>
                  {item} ×
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_featured} onCheckedChange={v => setForm({...form, is_featured: v})} />
            <Label>Featured Exhibitor</Label>
          </div>
        </TabsContent>
        
        <TabsContent value="booth" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Booth Number</Label><Input value={form.booth_number || ''} onChange={e => setForm({...form, booth_number: e.target.value})} placeholder="A-101" /></div>
            <div><Label>Booth Size</Label>
              <Select value={form.booth_size || 'medium'} onValueChange={v => setForm({...form, booth_size: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BOOTH_SIZES.map(s => <SelectItem key={s} value={s}>{s} ({SIZE_LABELS[s]})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4" />
              Booth Location (Optional)
            </Label>
            <GoogleMapPicker
              latitude={form.latitude}
              longitude={form.longitude}
              address={form.address || ''}
              onLocationChange={(location) => {
                setForm(prev => ({
                  ...prev,
                  latitude: location.lat,
                  longitude: location.lng,
                  address: location.address || prev.address
                }));
              }}
              height="250px"
              placeholder="Search for booth location..."
            />
          </div>
        </TabsContent>
        
        <TabsContent value="contact" className="space-y-4">
          <div><Label>Contact Name</Label><Input value={form.contact_name || ''} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
          <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} type="email" /></div>
          <div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} /></div>
        </TabsContent>
      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t mt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.company_name}>{exhibitor ? 'Update' : 'Create'} Exhibitor</Button>
      </div>
    </Tabs>
  );
};

const ExhibitorBooths = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingExhibitor, setEditingExhibitor] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const { data: exhibitors = [], isLoading } = useQuery({
    queryKey: ['exhibitors', eventId],
    queryFn: () => exhibitorAPI.adminList(eventId).then(r => Array.isArray(r.data) ? r.data : []),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['exhibitor-categories', eventId],
    queryFn: () => exhibitorAPI.categories(eventId).then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
  });

  const filteredExhibitors = filterCategory === 'all' 
    ? exhibitors 
    : exhibitors.filter(e => e.category === filterCategory);

  const createMutation = useMutation({
    mutationFn: (data) => exhibitorAPI.create(eventId, data),
    onSuccess: () => { toast.success('Exhibitor created'); queryClient.invalidateQueries(['exhibitors', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(err.response?.data?.errors || 'Failed to create')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => exhibitorAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Exhibitor updated'); queryClient.invalidateQueries(['exhibitors', eventId]); setEditingExhibitor(null); },
    onError: (err) => toast.error(err.response?.data?.errors || 'Failed to update')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => exhibitorAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Exhibitor deleted'); queryClient.invalidateQueries(['exhibitors', eventId]); },
    onError: (err) => toast.error(err.response?.data?.errors || 'Failed to delete')
  });

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="exhibitor-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6" />Exhibitors</h2>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.filter(c => c && c.trim()).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Badge variant="outline">{filteredExhibitors.length} exhibitors</Badge>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Exhibitor</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add Exhibitor</DialogTitle></DialogHeader>
            <ExhibitorForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredExhibitors.map(exhibitor => (
          <Card key={exhibitor.id} data-testid={`exhibitor-${exhibitor.id}`} className={exhibitor.is_featured ? 'border-yellow-400' : ''}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {exhibitor.logo_url ? (
                    <img src={exhibitor.logo_url} alt={exhibitor.company_name} className="w-12 h-12 rounded object-cover" />
                  ) : (
                    <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-slate-500" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {exhibitor.company_name}
                      {exhibitor.is_featured && <Badge variant="secondary" className="bg-yellow-100">Featured</Badge>}
                    </CardTitle>
                    {exhibitor.category && <Badge variant="outline" className="text-xs">{exhibitor.category}</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Dialog open={editingExhibitor?.id === exhibitor.id} onOpenChange={(open) => !open && setEditingExhibitor(null)}>
                    <DialogTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={() => setEditingExhibitor(exhibitor)}><Pencil className="h-4 w-4" /></Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Edit Exhibitor</DialogTitle></DialogHeader>
                      <ExhibitorForm 
                        exhibitor={editingExhibitor} 
                        onSubmit={data => updateMutation.mutate({ id: exhibitor.id, data })} 
                        onCancel={() => setEditingExhibitor(null)} 
                      />
                    </DialogContent>
                  </Dialog>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => deleteMutation.mutate(exhibitor.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2 text-sm text-muted-foreground space-y-2">
              {exhibitor.booth_number && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Booth {exhibitor.booth_number}</span>
                  {exhibitor.booth_size && <Badge variant="secondary">{exhibitor.booth_size}</Badge>}
                </div>
              )}
              {exhibitor.description && <p className="line-clamp-2">{exhibitor.description}</p>}
              {exhibitor.products_services?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {exhibitor.products_services.slice(0, 3).map((p, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{p}</Badge>
                  ))}
                  {exhibitor.products_services.length > 3 && (
                    <Badge variant="outline" className="text-xs">+{exhibitor.products_services.length - 3} more</Badge>
                  )}
                </div>
              )}
              {exhibitor.website_url && (
                <a href={exhibitor.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                  <Globe className="h-3 w-3" /> Website
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredExhibitors.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No exhibitors yet. Add your first exhibitor!</p>
        </Card>
      )}
    </div>
  );
};

export default ExhibitorBooths;
